var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class JblSynthesis {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.framework = framework;
        // other class properties and constructor
        // Translate equipmentType source codes to RC5 IR codes by zone.
        this.sourceLookup = {
            '1': {
                // zone 1 values
                '1': 0x76,
                '2': 0x62,
                '3': 0x5e,
                '4': 0x1b,
                '5': 0x60,
                '6': 0x7d,
                '8': 0x63,
                '9': 0x3a,
                '11': 0x1c,
                '12': 0x48,
                '14': 0x5c,
                '16': 0x64,
                '17': 0x61,
                '18': 0x7a, // BT
            },
            '2': {
                // zone 2 values
                '1': 0x06,
                '2': 0x07,
                '3': 0x09,
                '4': 0x14,
                '5': 0x0f,
                '6': 0x17,
                '8': 0x0d,
                // no display source
                '11': 0x1e,
                '12': 0x10,
                '14': 0x13,
                '16': 0x08,
                '17': 0x0b,
                '18': 0x16, // BT
            },
        };
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: this.makeCommand([0x01, 0x00, 0x01, 0xf0]),
                next: /![\S\s]\x00[\S\s]\x01[\S\s]\x0d/i,
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x04, 0x01, 0xf1]), // request host version
                /!\x01\x04[\S\s]\x03([\S\s])([\S\s]{2})\x0d/i, tags);
                if (match[1].charCodeAt(0) === 0) {
                    return `${match[2].charCodeAt(0)}.${match[2].charCodeAt(1)}`;
                }
                return 'unknown';
            }),
            powerOffRestrictions: ['get'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // assumes zone 1
                const match = yield this.socket.send(this.makeCommand([0x01, 0x00, 0x01, 0xf0]), /!\x01\x00([\S\s])\x01([\S\s])\x0d/i, tags);
                return match[1].charCodeAt(0) === 0
                    ? match[2].charCodeAt(0) === 1
                    : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x08, 0x02, 0x10, power ? 0x7b : 0x7c]), /!\x01\x08([\S\s])\x02\x10([\x7b\x7c\x0c])\x0d/, tags, { retryAttempts: 3 } // sometimes receiver doesn't power off with room power off
                );
                if (match[1].charCodeAt(0) === 0) {
                    const powerState = match[2].charCodeAt(0) === 0x7b;
                    if (powerState) {
                        this.state.busy.update(true);
                        this.busyTimeout.set(() => this.state.busy.update(false), 1000);
                        this.framework.suspend(1000);
                    }
                    return powerState;
                }
                return this.state.power.value;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                // key corresponds to zone 1 or zone 2
                const keyInt = parseInt(key, 10) & 0xff;
                const match = yield this.socket.send(this.makeCommand([keyInt, 0x1d, 0x01, 0xf0]), /\x21[\S\s]\x1d[\S\s]\x01([\S\s])\x0d/, tags);
                return match[1].charCodeAt(0).toString();
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                // setting the source requires a different value based on the zone
                const srcCode = (_a = this.sourceLookup[key][value]) !== null && _a !== void 0 ? _a : 0x62;
                if (srcCode) {
                    // JBL receiver doesn't seem to send a response to the set command
                    yield this.socket.sendAsync(this.makeCommand([
                        parseInt(key, 10),
                        0x08,
                        0x02,
                        key === '1' ? 0x10 : 0x17,
                        srcCode, // RC5 command code
                    ]), tags);
                    return value;
                }
                return this.state.videoMatrixRoutes.values[key];
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x0d, 0x01, 0xf0]), /![\S\s]\x0D[\S\s]\x01([\S\s])\x0d/i, tags);
                return match[1].charCodeAt(0);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x0d, 0x01, volume]), /![\S\s]\x0D[\S\s]\x01([\S\s])\x0d/i, tags);
                return match[1].charCodeAt(0);
            }),
            range: { max: 99, min: 30, round: 1 },
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x0e, 0x01, 0xf0]), /![\S\s]\x0E[\S\s]\x01([\S\s])\x0d/i, tags);
                return match[1].charCodeAt(0) === 0;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x08, 0x02, 0x10, mute ? 0x1a : 0x78]), /![\S\s]\x08[\S\s]\x02\x10([\x1a\x78])\x0d/, tags);
                return match[1].charCodeAt(0) === 0x1a;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                onePerInterval: true,
                controls: ['power', 'volume', 'videoMatrixRoutes', 'volumeMute'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
    // other class methods
    makeCommand(command) {
        const data = [0x21]; // command header
        data.push(...command);
        return AsciiText.decode(data);
    }
}
// static properties
JblSynthesis.driverType = 'socketDriver';
JblSynthesis.key = '-O46HBLHvrLrJk2X58iB';
JblSynthesis.baseSocketOptions = {
    commandExtractor: {
        terminators: [],
        unterminatedRegExp: /\x21[\x01\x02][\S\s][\S\s][\S\s]{1,4}\x0d/i,
    },
    idleTimeoutMs: 25000,
    isBinary: true,
    sendTerminator: '\x0d',
};
JblSynthesis.socketOptions = [
    Object.assign(Object.assign({}, JblSynthesis.baseSocketOptions), { port: 50000 }),
    Object.assign(Object.assign({}, JblSynthesis.baseSocketOptions), { port: 4999 }),
];
